/**
 * UUIDv7 — time-ordered identifiers.
 *
 * Chosen over UUIDv4 because rows arrive in creation order on disk, which keeps
 * B-tree inserts local and makes `order by id` a usable proxy for `order by
 * created_at` in the delta-sync queries (see SPEC §7).
 */

const HEX: string[] = Array.from({ length: 256 }, (_, i) =>
  i.toString(16).padStart(2, "0"),
);

export function uuidv7(now: number = Date.now()): string {
  const bytes = new Uint8Array(16);

  // 48-bit big-endian unix timestamp in milliseconds
  bytes[0] = (now / 2 ** 40) & 0xff;
  bytes[1] = (now / 2 ** 32) & 0xff;
  bytes[2] = (now / 2 ** 24) & 0xff;
  bytes[3] = (now / 2 ** 16) & 0xff;
  bytes[4] = (now / 2 ** 8) & 0xff;
  bytes[5] = now & 0xff;

  crypto.getRandomValues(bytes.subarray(6));

  // version 7, variant RFC 4122
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;

  const h = (i: number): string => HEX[bytes[i]!]!;

  return (
    h(0) + h(1) + h(2) + h(3) + "-" +
    h(4) + h(5) + "-" +
    h(6) + h(7) + "-" +
    h(8) + h(9) + "-" +
    h(10) + h(11) + h(12) + h(13) + h(14) + h(15)
  );
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

/** Milliseconds encoded in a v7 id. Returns null for any other UUID version. */
export function uuidv7Timestamp(id: string): number | null {
  if (!isUuid(id) || id[14] !== "7") return null;
  return parseInt(id.slice(0, 8) + id.slice(9, 13), 16);
}

/** Invite codes are read aloud and retyped, so the alphabet excludes 0/O/1/I/L. */
const INVITE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

export function inviteCode(length = 10): string {
  const values = new Uint8Array(length);
  crypto.getRandomValues(values);
  let out = "";
  for (let i = 0; i < length; i++) {
    out += INVITE_ALPHABET[values[i]! % INVITE_ALPHABET.length];
    if (i === 4 && length > 5) out += "-";
  }
  return out;
}
